import React from 'react';

/**
 * Lightweight inline markdown renderer.
 * Handles: bold, italic, strikethrough, inline code, links.
 */
export const renderInline = (text: string): React.ReactNode => {
  const parts = text.split(
    /(\*\*[^*]+\*\*|\*[^*]+\*|~~[^~]+~~|`[^`]+`|\[[^\]]+\]\([^)]+\))/g
  );
  return (
    <>
      {parts.map((part, idx) => {
        if (part.startsWith('**') && part.endsWith('**'))
          return <strong key={idx}>{part.slice(2, -2)}</strong>;
        if (part.startsWith('*') && part.endsWith('*'))
          return <em key={idx}>{part.slice(1, -1)}</em>;
        if (part.startsWith('~~') && part.endsWith('~~'))
          return <del key={idx}>{part.slice(2, -2)}</del>;
        if (part.startsWith('`') && part.endsWith('`'))
          return (
            <code
              key={idx}
              className="bg-gray-100 px-1 py-0.5 rounded font-mono text-sm text-rose-600"
            >
              {part.slice(1, -1)}
            </code>
          );
        const link = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
        if (link)
          return (
            <a
              key={idx}
              href={link[2]}
              className="text-blue-600 underline hover:text-blue-800"
              target="_blank"
              rel="noopener noreferrer"
            >
              {link[1]}
            </a>
          );
        return <span key={idx}>{part}</span>;
      })}
    </>
  );
};

/**
 * Render a paragraph string: split on newlines, apply inline formatting per line.
 */
export const renderParagraph = (text: string): React.ReactNode => {
  const lines = text.split('\n');
  return (
    <>
      {lines.map((line, i) => (
        <React.Fragment key={i}>
          {renderInline(line)}
          {i < lines.length - 1 && <br />}
        </React.Fragment>
      ))}
    </>
  );
};
